/** @file The mesh the mesh tools edit: the first selected slot showing a mesh, else a selected vertex's mesh. */
import type { Doc, Id, MeshAtt } from '@pose2d/format/types';
import type { SelectionSlice } from '../../stores/types';

/** A mesh attachment and the slot it is edited through (whose bone carries a rigid mesh). */
export interface MeshTarget {
  slot: Id;
  attachment: Id;
  mesh: MeshAtt;
}

/** The attachment id a slot shows (setup skin or the posed viewport), or null for an empty slot. */
export type ShownAttachment = (slot: Id) => Id | null;

/** The mesh record `att` when it is a mesh, else undefined. */
function meshOf(doc: Doc, att: Id | null): MeshAtt | undefined {
  const record = att === null ? undefined : doc.attachments[att];
  return record?.type === 'mesh' ? record : undefined;
}

/**
 * The first selected slot (selection order) whose shown attachment is a mesh; else the mesh of the first selected
 * vertex, edited through the first slot in draw order that shows it; null when neither exists.
 */
export function meshTarget(doc: Doc, selection: SelectionSlice, shown: ShownAttachment): MeshTarget | null {
  for (const slot of selection.slots) {
    const attachment = doc.slots[slot] === undefined ? null : shown(slot);
    const mesh = meshOf(doc, attachment);
    if (attachment !== null && mesh !== undefined) return { slot, attachment, mesh };
  }
  const first = selection.vertices[0];
  const mesh = first === undefined ? undefined : meshOf(doc, first[0]);
  if (first === undefined || mesh === undefined) return null;
  const slot = doc.drawOrder.find((s) => shown(s) === first[0]);
  return slot === undefined ? null : { slot, attachment: first[0], mesh };
}
